import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";

type WirelessUser = {
  userId: number;
  organization: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
};

type SortKey = "organization" | "firstName" | "lastName" | "email" | "phone" | "role";

const PAGE_SIZE = 50;

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

const COLUMNS: HubDataTableColumn<WirelessUser, SortKey>[] = [
  { id: "organization", header: "Organization", sortKey: "organization", cell: (row) => row.organization },
  { id: "firstName", header: "First name", sortKey: "firstName", overflow: "nowrap", cell: (row) => row.firstName },
  { id: "lastName", header: "Last name", sortKey: "lastName", overflow: "nowrap", cell: (row) => row.lastName },
  { id: "email", header: "Email", sortKey: "email", cell: (row) => row.email },
  { id: "phone", header: "Phone number", sortKey: "phone", overflow: "nowrap", cell: (row) => row.phone },
  { id: "role", header: "Role", sortKey: "role", overflow: "nowrap", cell: (row) => row.role },
];

export function WirelessUsersPage() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("organization");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const usersQuery = useQuery({
    queryKey: ["wireless", "users"],
    queryFn: () => apiFetch<{ users: WirelessUser[]; csv: string }>("/api/wireless/users"),
  });

  const filtered = useMemo(() => {
    const users = usersQuery.data?.users ?? [];
    const needle = query.trim().toLowerCase();
    const matched =
      needle === ""
        ? users
        : users.filter((user) =>
            [user.organization, user.firstName, user.lastName, user.email, user.phone, user.role]
              .join(" ")
              .toLowerCase()
              .includes(needle),
          );
    const sorted = [...matched].sort((left, right) => {
      const result = compareText(left[sortKey], right[sortKey]);
      return sortDir === "asc" ? result : -result;
    });
    return sorted;
  }, [query, sortDir, sortKey, usersQuery.data?.users]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  function downloadCsv() {
    const csv = usersQuery.data?.csv;
    if (!csv) return;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "wireless-users.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Every active wireless portal user, including organization, contact details, and portal role. Download CSV includes the full directory, not only the rows on this page.">
          Users
        </PageTitleWithHelp>
        <Button type="button" onClick={downloadCsv} disabled={!usersQuery.data?.csv}>
          Download CSV
        </Button>
      </div>

      <Card>
        <CardBody className="py-3">
          <label className="block text-xs font-medium uppercase tracking-wide text-slate-600" htmlFor="wireless-user-search">
            Search
          </label>
          <Input
            id="wireless-user-search"
            value={query}
            placeholder="Organization, name, email, phone, or role"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
            className="mt-1 max-w-md"
          />
        </CardBody>
      </Card>

      <HubDataTable
        columns={COLUMNS}
        rows={visible}
        rowKey={(row) => row.userId}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={usersQuery.isLoading}
        loadingMessage="Loading users..."
        error={usersQuery.isError ? "Could not load wireless portal users." : null}
        empty="No users."
        isFiltered={query.trim() !== ""}
        filteredEmpty="No rows match these filters."
      />

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          {filtered.length} user{filtered.length === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" disabled={safePage <= 0} onClick={() => setPage(safePage - 1)}>
            Previous
          </Button>
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
